'use client';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Link from 'next/link';

import { countryConfig } from '@/app/utils/countryConfig';

interface MenuItem {
  title: string;
  path: string;
}

export interface FooterMenuSection {
  title: string;
  items: MenuItem[];
}

const FooterNavigation = () => {
  const { t } = useTranslation();

  const menuSections: FooterMenuSection[] = useMemo(
    () =>
      countryConfig.footerNavigationSections.map((section) => ({
        title: t(section.titleTranslationKey),
        items: section.items.map((item) => ({
          title: t(item.titleTranslationKey),
          path: item.path,
        })),
      })),
    [countryConfig, t],
  );

  // TODO Temporary workaround
  const prefixMenuItemsPaths = useCallback((sections: FooterMenuSection[]): FooterMenuSection[] => {
    const baseUrl = process.env.NEXT_PUBLIC_REDIRECT_URL;

    return sections.map((section) => {
      return {
        ...section,
        items: section.items.map((item) => ({
          ...item,
          path: baseUrl?.endsWith('/') ? `${baseUrl}${item.path}` : `${baseUrl}/${item.path}`,
        })),
      };
    });
  }, []);

  const prefixedMenuSections = useMemo(() => prefixMenuItemsPaths(menuSections), [menuSections, prefixMenuItemsPaths]);

  return (
    <div className="lg:max-h-[440px] flex flex-col flex-wrap">
      {prefixedMenuSections.map((section) => (
        <nav key={section.title} className="lg:w-[25%] mb-[56px]">
          <span className="block font-medium leading-[24px] mb-[16px]">{section.title}</span>
          <ul>
            {section.items.map((item) => (
              <li key={item.title} className="relative">
                <Link
                  prefetch={process.env.NEXT_PUBLIC_PREFETCH_LINKS === 'true'}
                  className="mb-[16px] hover:text-primary-mid flex items-center"
                  href={item.path}
                >
                  <span>{item.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      ))}
    </div>
  );
};

export default FooterNavigation;
